import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Users, Heart, MessageSquare, DollarSign, Loader2, RefreshCw, UserCheck, Crown, Calendar } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminAnalytics = () => {
  const [period, setPeriod] = useState('30d');
  const [engagement, setEngagement] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    setError(null);

    try {
      const [engagementRes, revenueRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/phase2/reports/engagement?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/admin/phase2/reports/revenue?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setEngagement(engagementRes.data);
      setRevenue(revenueRes.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const StatCard = ({ icon: Icon, label, value, subValue, color = 'mint' }) => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
        </div>
        <div className={`p-3 bg-${color}-50 rounded-lg`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  const ProgressBar = ({ label, value, max, percentage }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">{label}</span>
        <span className="font-medium text-gray-900">{value} ({percentage}%)</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-mint-500 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600 mt-1">Track user engagement and revenue metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
            data-testid="period-select"
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
            <option value="1y">Last Year</option>
          </select>
          <button
            onClick={fetchAnalytics}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Revenue Section */}
      {revenue && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            Revenue Overview
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={DollarSign}
              label="Total Revenue"
              value={`$${revenue.total_revenue?.toLocaleString() || 0}`}
              subValue={`${revenue.transaction_count} transactions`}
              color="green"
            />
            <StatCard
              icon={TrendingUp}
              label="Avg Transaction"
              value={`$${revenue.average_transaction || 0}`}
              color="blue"
            />
            <StatCard
              icon={Crown}
              label="Premium Users"
              value={revenue.premium_users || 0}
              color="amber"
            />
            <StatCard
              icon={Calendar}
              label="Period"
              value={period === '7d' ? '7 Days' : period === '30d' ? '30 Days' : period === '90d' ? '90 Days' : '1 Year'}
              color="gray"
            />
          </div>

          {/* Daily Revenue Chart (Simple) */}
          {revenue.daily_revenue && revenue.daily_revenue.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Daily Revenue</h3>
              <div className="flex items-end gap-1 h-32">
                {revenue.daily_revenue.slice(-14).map((day, index) => {
                  const maxAmount = Math.max(...revenue.daily_revenue.map(d => d.amount)) || 1;
                  const height = (day.amount / maxAmount) * 100;
                  return (
                    <div
                      key={day.date}
                      className="flex-1 bg-mint-500 rounded-t hover:bg-mint-600 transition-colors cursor-pointer group relative"
                      style={{ height: `${Math.max(height, 5)}%` }}
                      title={`${day.date}: $${day.amount}`}
                    >
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        ${day.amount}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>{revenue.daily_revenue[0]?.date}</span>
                <span>{revenue.daily_revenue[revenue.daily_revenue.length - 1]?.date}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Engagement Section */}
      {engagement && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-mint-500" />
            User Engagement
          </h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              icon={Users}
              label="Total Users"
              value={engagement.total_users?.toLocaleString() || 0}
              subValue={`+${engagement.new_users || 0} new`}
            />
            <StatCard
              icon={UserCheck}
              label="Active Users"
              value={engagement.active_users?.toLocaleString() || 0}
              subValue={`${engagement.activation_rate || 0}% activation`}
              color="blue"
            />
            <StatCard
              icon={MessageSquare}
              label="Messages Sent"
              value={engagement.messages_sent?.toLocaleString() || 0}
            />
            <StatCard
              icon={Heart}
              label="Interests Sent"
              value={engagement.interests_sent?.toLocaleString() || 0}
              color="rose"
            />
          </div>

          {/* Detailed Metrics */}
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4">User Metrics</h3>
              <div className="space-y-4">
                <ProgressBar
                  label="Profile Completion (with photo)"
                  value={engagement.profile_completion?.with_photo || 0}
                  max={engagement.total_users}
                  percentage={engagement.profile_completion?.photo_rate || 0}
                />
                <ProgressBar
                  label="Verified Users"
                  value={engagement.verification?.verified || 0}
                  max={engagement.total_users}
                  percentage={engagement.verification?.verification_rate || 0}
                />
                <ProgressBar
                  label="Premium Conversion"
                  value={engagement.premium?.count || 0}
                  max={engagement.total_users}
                  percentage={engagement.premium?.conversion_rate || 0}
                />
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-sm font-medium text-gray-700 mb-4">Gender Distribution</h3>
              <div className="flex items-center justify-center gap-8">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center mb-2">
                    <span className="text-2xl font-bold text-blue-600">
                      {engagement.gender_distribution?.male_percentage || 0}%
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-700">Male</p>
                  <p className="text-xs text-gray-500">{engagement.gender_distribution?.male || 0} users</p>
                </div>
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-pink-100 flex items-center justify-center mb-2">
                    <span className="text-2xl font-bold text-pink-600">
                      {(100 - (engagement.gender_distribution?.male_percentage || 0)).toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-700">Female</p>
                  <p className="text-xs text-gray-500">{engagement.gender_distribution?.female || 0} users</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminAnalytics;
